import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { useTranslation } from '../../../i18n';
import { MaintenanceRecord, MaintenanceRecordCreate, MaintenanceStatus } from '../../../types/api';
import { maintenanceKeys } from './maintenance.queries';

// STD-015/016: imperative/write actions for the MaintenanceTab. apiService is
// imported here (a *.mutations.ts file) per STD-016. The global MutationCache
// handler toasts on error using meta.errorMessage.
//
// These mutations replace the hand-rolled `window` CustomEvent bus that used to
// signal the upcoming/history query hooks: each mutation invalidates the
// relevant maintenanceKeys in onSuccess and TanStack auto-refetches.

// Invalidate both the per-drone history and upcoming lists. Mutations touch one
// record but either list may now be stale, so refresh both.
function invalidateDroneMaintenance(
  queryClient: ReturnType<typeof useQueryClient>,
  droneSerial: string
) {
  queryClient.invalidateQueries({ queryKey: maintenanceKeys.history(droneSerial) });
  queryClient.invalidateQueries({ queryKey: maintenanceKeys.upcoming(droneSerial) });
}

export function useOpenMaintenanceManualMutation() {
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('maintenanceTab.failedToOpenManual') },
    mutationFn: (manualFile: string) => apiService.getManualUrl(manualFile),
  });
}

// Delete a maintenance record — invalidates the drone's history + upcoming lists
// so both panels refresh.
export function useDeleteMaintenanceRecordMutation() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    meta: { errorMessage: t('maintenanceTab.deleteFailed') },
    mutationFn: async ({ recordId }: { recordId: string; droneSerial: string }) => {
      return apiService.deleteMaintenanceRecord(recordId);
    },
    onSuccess: (_data, variables) => {
      invalidateDroneMaintenance(queryClient, variables.droneSerial);
    },
  });
}

// Fetch a fresh presigned download URL for an attachment key — called on
// user-click in AttachmentList. Returns the { download_url } shape.
export function useGetAttachmentDownloadUrlMutation() {
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('maintenanceTab.failedToDownloadAttachment') },
    mutationFn: async (s3Key: string) => {
      return apiService.getAttachmentDownloadUrl(s3Key);
    },
  });
}

// Create a new maintenance type inline (from the create-record dialog combobox).
// Invalidates the types list so the new option appears everywhere it's read.
export function useCreateMaintenanceTypeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (label: string) => apiService.createMaintenanceType(label),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: maintenanceKeys.types() });
    },
  });
}

// Upload a single attachment file, returning its S3 key. Used by the create flow
// before the record itself is created; no cache invalidation (no list changes
// until the record is created).
export function useUploadMaintenanceAttachmentMutation() {
  return useMutation({
    mutationFn: ({ file, droneSerial }: { file: File; droneSerial: string }) =>
      apiService.uploadAttachment(file, droneSerial),
  });
}

// Create one or more maintenance records (backend returns one per selected type).
// Invalidates the drone's history + upcoming lists on success.
export function useCreateMaintenanceRecordMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (record: MaintenanceRecordCreate) => apiService.createMaintenanceRecord(record),
    onSuccess: (_data, record) => {
      invalidateDroneMaintenance(queryClient, record.drone_serial);
    },
  });
}

// --- Per-field update mutations (consumed by useMaintenanceRecordOperations) ---
// Each carries droneSerial in its variables purely so onSuccess can invalidate
// the right per-drone keys; the API call itself only needs recordId + the value.

export function useUpdateMaintenanceStatusMutation() {
  const queryClient = useQueryClient();
  return useMutation<
    MaintenanceRecord,
    unknown,
    { recordId: string; status: MaintenanceStatus; droneSerial: string }
  >({
    mutationFn: ({ recordId, status }) => apiService.updateMaintenanceStatus(recordId, status),
    onSuccess: (_data, { droneSerial }) => {
      invalidateDroneMaintenance(queryClient, droneSerial);
    },
  });
}

export function useUpdateMaintenanceDateMutation() {
  const queryClient = useQueryClient();
  return useMutation<
    MaintenanceRecord,
    unknown,
    { recordId: string; startDate: string; endDate: string; droneSerial: string }
  >({
    mutationFn: ({ recordId, startDate, endDate }) =>
      apiService.updateMaintenanceDate(recordId, startDate, endDate),
    onSuccess: (_data, { droneSerial }) => {
      invalidateDroneMaintenance(queryClient, droneSerial);
    },
  });
}

export function useUpdateMaintenanceContentMutation() {
  const queryClient = useQueryClient();
  return useMutation<
    MaintenanceRecord,
    unknown,
    { recordId: string; workDescription: string; droneSerial: string }
  >({
    mutationFn: ({ recordId, workDescription }) =>
      apiService.updateMaintenanceContent(recordId, workDescription),
    onSuccess: (_data, { droneSerial }) => {
      invalidateDroneMaintenance(queryClient, droneSerial);
    },
  });
}

export function useUpdateMaintenanceQualityCheckMutation() {
  const queryClient = useQueryClient();
  return useMutation<
    MaintenanceRecord,
    unknown,
    { recordId: string; qualityChecked: boolean; droneSerial: string }
  >({
    mutationFn: ({ recordId, qualityChecked }) =>
      apiService.updateMaintenanceQualityCheck(recordId, qualityChecked),
    onSuccess: (_data, { droneSerial }) => {
      invalidateDroneMaintenance(queryClient, droneSerial);
    },
  });
}
